import { TextFieldProps } from '@mui/material';
import { DateTimePicker, DateTimePickerProps } from '@mui/x-date-pickers';
import { useField, useFormikContext } from 'formik';
import moment, { Moment, unitOfTime } from 'moment';
import { useCallback } from 'react';

import { DateFormat } from 'enums';

type Props = {
  name: string;
  startOf?: unitOfTime.StartOf;
  endOf?: unitOfTime.StartOf;
  clearable?: boolean;
} & Omit<DateTimePickerProps<Moment>, 'onChange' | 'value'> &
  Partial<TextFieldProps>;

export const FormikDateTimePicker: React.FC<Props> = ({
  name,
  startOf,
  endOf,
  minDate,
  maxDate,
  clearable,
  ...restProps
}) => {
  const [field] = useField(name);
  const { setFieldValue } = useFormikContext();

  const handleChange = useCallback(
    (value: Moment | null) => {
      if (value) {
        if (startOf) {
          value = moment(value).startOf(startOf);
        } else if (endOf) {
          value = moment(value).endOf(endOf);
        }
      }
      setFieldValue(name, value);
    },
    [name, startOf, endOf, setFieldValue],
  );

  return (
    <DateTimePicker
      minDate={moment(minDate || 0)}
      maxDate={maxDate ? moment(maxDate) : undefined}
      viewRenderers={{
        hours: null,
        minutes: null,
        seconds: null,
      }}
      slotProps={{
        textField: {
          variant: restProps?.variant || 'standard',
          size: restProps?.size,
          helperText: restProps?.helperText,
        },
        field: clearable ? { clearable: true } : {},
      }}
      format={DateFormat.Long}
      {...restProps}
      value={field.value ? moment(field.value) : null}
      onChange={handleChange}
    />
  );
};
